import React, { FC, ReactElement } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
// Components
import { clsx } from 'clsx';
import ModalButton, { ButtonType } from './templates/modalButton';
import ModalButtonContainer from './templates/modalButtonContainer';
import Text, { TextComponentProps } from '../text';
import Separator from '../layout/separator';
// Resources
import { stringsRes } from '../../resources/strings';
import { PropsFromModal } from './createModalHOC';
import useGlobalStyles from '../hook/useGlobalStyles';

interface ConfirmationProps extends PropsFromModal {
    description?: string;
    descriptionProps?: TextComponentProps;
    labelCancel?: string;
    typeCancel?: ButtonType;
    noCancel?: boolean;
    labelConfirm?: string;
    typeConfirm?: ButtonType;
    onConfirmAction?: () => void | Promise<unknown>;
    onCancelAction?: () => void;
    customView?: ReactElement;
    containerClassName?: string;
}

const Confirmation: FC<ConfirmationProps> = (props): ReactElement => {
    const {
        onRequestClose,
        description,
        descriptionProps,
        labelCancel,
        typeCancel,
        noCancel,
        labelConfirm,
        typeConfirm,
        onConfirmAction,
        onCancelAction,
        customView,
        containerClassName,
    } = props;
    const classes = useStyle();
    const globalClasses = useGlobalStyles();

    const [isConfirming, setIsConfirming] = React.useState<boolean>(false);

    const onClickConfirm = (): void => {
        const result = onConfirmAction?.();

        // An action that answers with a promise keeps the modal up, the confirm button turned into a
        // loader: closing right away left the user in front of the previous screen with no idea the
        // request was still running. A failure keeps it open too, so the action can be retried - the
        // error toast says what went wrong.
        if (typeof (result as Promise<unknown>)?.then === 'function') {
            setIsConfirming(true);
            (result as Promise<unknown>).then(
                () => {
                    setIsConfirming(false);
                    onRequestClose();
                },
                () => setIsConfirming(false),
            );
            return;
        }

        onRequestClose();
    };

    const onClickCancel = (): void => {
        if (onCancelAction) {
            onCancelAction();
        }
        onRequestClose();
    };

    return (
        <div
            className={clsx(
                classes.confirmationContainer,
                noCancel && classes.confirmationActionFullWidth,
                containerClassName,
            )}
        >
            <div className={globalClasses.flexCol}>
                <Text.Description16 text="" dangerouslySetInnerHTML={{ __html: description }} {...descriptionProps} />
            </div>
            {customView}
            <Separator height={1} />
            <ModalButtonContainer>
                {!noCancel && (
                    <ModalButton
                        type={typeCancel ?? ButtonType.CANCEL}
                        onClick={onClickCancel}
                        label={labelCancel ?? stringsRes.common.back}
                        disabled={isConfirming}
                    />
                )}
                <ModalButton
                    type={typeConfirm ?? ButtonType.ERROR}
                    onClick={onClickConfirm}
                    label={labelConfirm ?? stringsRes.profile.logout}
                    isLoading={isConfirming}
                    disabled={isConfirming}
                />
            </ModalButtonContainer>
        </div>
    );
};

const useStyle = makeStyles(() =>
    createStyles({
        confirmationContainer: {
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            '& > div:last-child': {
                marginTop: 12,
            },
        },
        confirmationActionFullWidth: {
            '& > div > button': {
                flex: 1,
                maxWidth: 'unset',
            },
        },
    }),
);

export default Confirmation;
